import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  FiClock, FiPlus, FiTrash2, FiX, FiCalendar, FiRefreshCw,
  FiCheckCircle, FiAlertCircle, FiPauseCircle, FiMail, FiUsers,
} from 'react-icons/fi';
import AppHeader from './AppHeader';
import { getValidAccessToken } from '../utils/microsoftAuth';
import { handleSessionExpired, isTokenExpiredError } from '../utils/auth';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  active: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
};

const STATUS_ICONS = {
  pending: <FiClock className="w-3 h-3" />,
  active: <FiRefreshCw className="w-3 h-3" />,
  completed: <FiCheckCircle className="w-3 h-3" />,
  failed: <FiAlertCircle className="w-3 h-3" />,
  cancelled: <FiPauseCircle className="w-3 h-3" />,
};

const CRON_PRESETS = [
  { label: 'Every day at 9am', value: '0 9 * * *' },
  { label: 'Every Monday at 9am', value: '0 9 * * 1' },
  { label: 'Every weekday at 9am', value: '0 9 * * 1-5' },
  { label: 'Every week (Mon 9am)', value: '0 9 * * 1' },
  { label: 'Every month (1st 9am)', value: '0 9 1 * *' },
  { label: 'Custom', value: 'custom' },
];

const getTimezones = () => {
  try {
    return Intl.supportedValuesOf('timeZone');
  } catch {
    return ['UTC', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'Europe/Berlin', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto', 'Australia/Sydney', 'Pacific/Auckland'];
  }
};

const formatTime = (date, tz) => {
  return new Date(date).toLocaleString('en-GB', {
    timeZone: tz, weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const EMPTY_FORM = {
  name: '', from_email: '', to_emails: '', subject: '', body: '',
  template_id: '', provider: 'microsoft',
  access_token: '',
  refresh_token: '',
  token_expires_at: '',
  schedule_type: 'once', scheduled_at: '', cron_expression: '', cron_preset: '0 9 * * *',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  staggered: false,
  interval_minutes: 5,
};

const ScheduledMailsPage = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const userEmail = (localStorage.getItem('ms_user_email') || '').toLowerCase();

  const loadSchedules = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (userEmail) params.append('user', userEmail);
      const res = await axios.get(`/api/scheduler${params.toString() ? `?${params.toString()}` : ''}`);
      setSchedules(res.data.schedules);
    } catch {
      toast.error('Failed to load schedules');
    } finally {
      setLoading(false);
    }
  }, [userEmail]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await axios.get('/api/templates');
      setTemplates(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadSchedules();
    loadTemplates();
    // Pre-fill token/provider from localStorage
    const msToken = localStorage.getItem('ms_access_token');
    const googleToken = localStorage.getItem('google_access_token');
    const provider = localStorage.getItem('active_provider') || 'microsoft';
    const fromEmail = localStorage.getItem('ms_user_email') || '';
    setForm(f => ({
      ...f,
      provider,
      access_token: provider === 'gmail' ? (googleToken || '') : (msToken || ''),
      from_email: fromEmail,
    }));
  }, [loadSchedules, loadTemplates]);

  const handleFormChange = (field, value) => {
    setForm(f => {
      const updated = { ...f, [field]: value };
      if (field === 'cron_preset' && value !== 'custom') {
        updated.cron_expression = value;
      }
      if (field === 'provider') {
        updated.access_token = value === 'gmail'
          ? (localStorage.getItem('google_access_token') || '')
          : (localStorage.getItem('ms_access_token') || '');
        updated.from_email = localStorage.getItem('ms_user_email') || '';
      }
      return updated;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const recipients = form.to_emails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean).map(e => ({ email: e, name: '' }));
      if (recipients.length === 0) {
        toast.error('At least one recipient email is required');
        setSaving(false);
        return;
      }

      // For Microsoft, refresh the token before scheduling so the stored token
      // in the schedule row is fresh and will last for the campaign.
      let accessToken = form.access_token;
      let refreshToken = localStorage.getItem('ms_refresh_token') || '';
      let tokenExpiresAt = localStorage.getItem('ms_token_expires_at') || '';

      if (form.provider !== 'gmail') {
        accessToken = await getValidAccessToken() || '';
        if (!accessToken) {
          setSaving(false);
          return; // handleSessionExpired already triggered inside refreshAccessToken
        }
        setForm(f => ({ ...f, access_token: accessToken }));
      }

      const recipientCount = recipients.length;
      if (form.schedule_type === 'once' && recipientCount > 1 && form.interval_minutes > 0) {
        if (!form.scheduled_at) {
          toast.error('Start time is required for scheduling');
          setSaving(false);
          return;
        }
        const payload = {
          name: form.name,
          from_email: form.from_email,
          recipients,
          subject: form.subject,
          body: form.body,
          template_id: form.template_id || null,
          provider: form.provider,
          access_token: accessToken,
          refresh_token: refreshToken,
          token_expires_at: tokenExpiresAt,
          timezone: form.timezone,
          start_at: new Date(form.scheduled_at).toISOString(),
          interval_minutes: form.interval_minutes,
        };
        const { data } = await axios.post('/api/scheduler/batch', payload);
        toast.success(`Created ${data.count} staggered schedules every ${form.interval_minutes} minutes`);
      } else {
        const payload = {
          name: form.name,
          from_email: form.from_email,
          recipients,
          subject: form.subject,
          body: form.body,
          template_id: form.template_id || null,
          provider: form.provider,
          access_token: accessToken,
          refresh_token: refreshToken,
          token_expires_at: tokenExpiresAt,
          schedule_type: form.schedule_type,
          scheduled_at: form.schedule_type === 'once' && form.scheduled_at
            ? new Date(form.scheduled_at).toISOString()
            : null,
          cron_expression: form.schedule_type === 'recurring' ? form.cron_expression : null,
          timezone: form.timezone,
        };
        await axios.post('/api/scheduler', payload);
        toast.success('Schedule created successfully!');
      }
      setShowForm(false);
      setForm(EMPTY_FORM);
      loadSchedules();
    } catch (err) {
      if (isTokenExpiredError(err)) {
        handleSessionExpired('Your Microsoft session has expired. Please sign in again.');
      } else {
        toast.error(err.response?.data?.error || 'Failed to create schedule');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this scheduled email?')) return;
    try {
      await axios.post(`/api/scheduler/${id}/cancel`);
      toast.success('Schedule cancelled');
      loadSchedules();
    } catch {
      toast.error('Failed to cancel schedule');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scheduled email permanently?')) return;
    try {
      await axios.delete(`/api/scheduler/${id}`);
      toast.success('Deleted');
      loadSchedules();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="min-h-screen bg-neutral-50">
      <AppHeader />

      <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-10 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-xl">
              <FiClock className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Scheduled Mails</h1>
              <p className="text-sm text-gray-500">{schedules.length} schedule{schedules.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-semibold">
            <FiPlus className="w-4 h-4" /> New Schedule
          </button>
        </div>

        {/* List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : schedules.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 text-center py-16 shadow-sm">
              <FiClock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No scheduled emails</p>
              <p className="text-gray-400 text-sm">Create a schedule to send emails at a specific time</p>
            </div>
          ) : schedules.map(s => (
            <div key={s.id} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{s.name}</h3>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status] || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS_ICONS[s.status]} {s.status}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.schedule_type === 'recurring' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                      {s.schedule_type === 'recurring' ? <><FiRefreshCw className="inline w-3 h-3 mr-1" />Recurring</> : <><FiCalendar className="inline w-3 h-3 mr-1" />One-time</>}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 font-medium truncate mb-1">
                    <FiMail className="inline w-3 h-3 mr-1 text-gray-400" />{s.subject}
                  </p>
                  <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                    <span><FiUsers className="inline w-3 h-3 mr-1" />{Array.isArray(s.recipients) ? s.recipients.length : 1} recipient(s)</span>
                    {s.schedule_type === 'once' && s.scheduled_at && (
                      <span><FiCalendar className="inline w-3 h-3 mr-1" />Scheduled: {formatDate(s.scheduled_at)}</span>
                    )}
                    {s.schedule_type === 'recurring' && s.cron_expression && (
                      <span><FiRefreshCw className="inline w-3 h-3 mr-1" />Cron: <code className="bg-gray-100 px-1 rounded">{s.cron_expression}</code></span>
                    )}
                    {s.last_run && <span>Last run: {formatDate(s.last_run)}</span>}
                    {s.run_count > 0 && <span>Runs: {s.run_count}</span>}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {(s.status === 'pending' || s.status === 'active') && (
                    <button onClick={() => handleCancel(s.id)}
                      className="px-3 py-1.5 text-xs border border-amber-300 text-amber-600 rounded-lg hover:bg-amber-50">
                      Cancel
                    </button>
                  )}
                  <button onClick={() => handleDelete(s.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Create Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-gray-900">New Scheduled Email</h2>
              <button onClick={() => setShowForm(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Schedule Name *</label>
                <input value={form.name} onChange={e => handleFormChange('name', e.target.value)} required
                  placeholder="e.g. Weekly Newsletter"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Email *</label>
                  <input value={form.from_email} disabled required
                    title="Locked to the signed-in account"
                    className="w-full px-3 py-2 border border-gray-200 bg-gray-100 text-gray-600 rounded-lg text-sm cursor-not-allowed" />
                  <p className="text-xs text-gray-500 mt-1">Locked to the signed-in account to prevent send failures.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
                  <select value={form.provider} onChange={e => handleFormChange('provider', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm bg-white">
                    <option value="microsoft">Microsoft</option>
                    <option value="gmail">Gmail</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipients (one email per line or comma-separated) *</label>
                <textarea value={form.to_emails} onChange={e => handleFormChange('to_emails', e.target.value)} required
                  rows={3} placeholder="user@example.com&#10;user2@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm resize-none" />
                <p className="text-xs text-gray-500 mt-1">
                  {form.to_emails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean).length <= 1
                    ? 'Enter at least 2 recipients to enable the staggered interval option.'
                    : `${form.to_emails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean).length} recipient(s) entered.`}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input value={form.subject} onChange={e => handleFormChange('subject', e.target.value)} required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template (optional)</label>
                <select value={form.template_id} onChange={e => handleFormChange('template_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm bg-white">
                  <option value="">No template — plain email</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Body</label>
                <textarea value={form.body} onChange={e => handleFormChange('body', e.target.value)}
                  rows={5} placeholder="Enter email content..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm resize-none" />
              </div>

              {/* Schedule Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type *</label>
                <div className="flex gap-3">
                  {['once', 'recurring'].map(type => (
                    <label key={type} className={`flex-1 flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-colors ${form.schedule_type === type ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <input type="radio" name="schedule_type" value={type} checked={form.schedule_type === type}
                        onChange={() => handleFormChange('schedule_type', type)} className="text-purple-600" />
                      <div>
                        <div className="text-sm font-medium text-gray-800">
                          {type === 'once' ? '⏰ One-time' : '🔄 Recurring'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {type === 'once' ? 'Send at a specific date & time' : 'Send on a repeating schedule'}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {form.schedule_type === 'once' && (
                <>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-800">
                    <strong>Note:</strong> Scheduled emails use your current sign-in token. Microsoft tokens expire after ~1 hour. For sends more than 1 hour in the future, sign in again shortly before the send time, or use the Email Composer to send immediately.
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Send At *</label>
                    <input type="datetime-local" value={form.scheduled_at}
                      onChange={e => handleFormChange('scheduled_at', e.target.value)} required
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm" />
                  </div>

                  {/* Interval for multiple recipients */}
                  {form.to_emails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean).length > 1 && (
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <label className="block text-sm font-medium text-gray-800 mb-1">Interval between emails (minutes)</label>
                      <div className="text-xs text-gray-500 mb-3">These {form.to_emails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean).length} recipients will be sent one at a time with this gap.</div>
                      <input
                        type="number"
                        min={0}
                        value={form.interval_minutes}
                        onChange={(e) => handleFormChange('interval_minutes', Math.max(0, parseInt(e.target.value, 10) || 0))}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
                      />
                      {form.scheduled_at && form.interval_minutes > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          Last email around{' '}
                          {new Date(new Date(form.scheduled_at).getTime() + (Math.max(0, form.to_emails.split(/[\n,]+/).map(e => e.trim()).filter(Boolean).length - 1)) * form.interval_minutes * 60000).toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                        </p>
                      )}
                      {form.scheduled_at && form.interval_minutes === 0 && (
                        <p className="text-xs text-amber-600 mt-2">All emails will be sent at the same time.</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sender Time Zone</label>
                    <select
                      value={form.timezone}
                      onChange={e => handleFormChange('timezone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm"
                    >
                      {getTimezones().map((tz) => (
                        <option key={tz} value={tz}>{tz}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Current time: {formatTime(new Date(), form.timezone)}</p>
                  </div>
                </>
              )}

              {form.schedule_type === 'recurring' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Recurrence</label>
                    <select value={form.cron_preset} onChange={e => handleFormChange('cron_preset', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm bg-white">
                      {CRON_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  {form.cron_preset === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Cron Expression *</label>
                      <input value={form.cron_expression} onChange={e => handleFormChange('cron_expression', e.target.value)}
                        placeholder="e.g. 0 9 * * 1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 text-sm font-mono" />
                      <p className="text-xs text-gray-400 mt-1">Format: minute hour day month weekday</p>
                    </div>
                  )}
                  {form.cron_preset !== 'custom' && (
                    <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                      Cron: <code className="font-mono">{form.cron_expression || form.cron_preset}</code>
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold text-sm disabled:opacity-50">
                  {saving ? 'Scheduling...' : 'Create Schedule'}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScheduledMailsPage;
